/**
 * Process Arrow — 3 numbered cards joined by drawing arrows. Final card glows.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { splitItem, sceneHeader } from "./_mockHelpers.js";

export const processArrow: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const eyebrow = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["Schritt 1|Anmelden", "Schritt 2|Profil", "Schritt 3|Live"]).slice(0, 4);
  const isVertical = ctx.format === "9:16";

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const cardIds = items.map((_, i) => `${clipPrefix}-c-${i}`);
  const arrowIds = items.slice(0, -1).map((_, i) => `${clipPrefix}-a-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const header = sceneHeader({ clipPrefix, accent, eyebrow, title: headline, titlePx: lg * 0.78, subPx: sm * 0.95 });

  let rowHtml = "";
  items.forEach((raw, i) => {
    const [title, desc] = splitItem(raw);
    const isLast = i === items.length - 1;
    rowHtml += `<div id="${cardIds[i]}" style="position:relative;${isVertical ? "" : "flex:1;"}background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${isLast ? withAlpha(accent, 0.55) : withAlpha(themeColors.fg, 0.14)};border-radius:32px;padding:28px;display:flex;flex-direction:column;gap:12px;min-height:180px;box-shadow:${isLast ? `0 0 60px ${withAlpha(accent, 0.5)}, 0 24px 60px rgba(0,0,0,0.35)` : `0 24px 60px rgba(0,0,0,0.35)`};">
      <div style="display:flex;align-items:center;gap:12px;">
        <div style="width:48px;height:48px;border-radius:9999px;background:${isLast ? accent : withAlpha(accent, 0.18)};color:${isLast ? themeColors.bg : accent};display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:22px;border:1.5px solid ${withAlpha(accent, 0.45)};box-shadow:${isLast ? `0 0 24px ${withAlpha(accent, 0.55)}` : "none"};">${i + 1}</div>
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.6}px;letter-spacing:3px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.6)};">Step ${i + 1}</div>
      </div>
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.7}px;letter-spacing:-1px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(title ?? "")}</div>
      ${desc ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.8}px;color:${withAlpha(themeColors.fg, 0.78)};line-height:1.4;">${escapeHtml(desc)}</div>` : ""}
    </div>`;
    if (i < items.length - 1) {
      rowHtml += `<div id="${arrowIds[i]}" style="display:flex;align-items:center;justify-content:center;${isVertical ? "padding:12px 0;" : "padding:0 8px;flex-shrink:0;"}">
        <svg width="${isVertical ? 30 : 60}" height="${isVertical ? 60 : 30}" viewBox="0 0 60 30" fill="none" stroke="${accent}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="${isVertical ? "transform:rotate(90deg);" : ""}">
          <path d="M2 15h54M44 4l12 11-12 11"/>
        </svg>
      </div>`;
    }
  });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:72px;">
    ${header.html}
    <div style="display:flex;flex-direction:${isVertical ? "column" : "row"};align-items:${isVertical ? "stretch" : "stretch"};gap:0;width:100%;max-width:${isVertical ? 760 : 1280}px;">${rowHtml}</div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${header.ids.eyebrow}`, { opacity: 0, y: 12 }) : "",
    headline ? gsapSetLine(`#${header.ids.title}`, { opacity: 0, y: 16 }) : "",
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 28, scale: 0.94 })),
    ...arrowIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.4 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${header.ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${header.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15));
  const stagger = Math.max(0.4, (clipDurationSec - 1.0) / Math.max(items.length, 1));
  cardIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.4)\"" }, 0.4 + i * stagger));
    if (arrowIds[i]) tweens.push(t.to(`#${arrowIds[i]}`, { opacity: 1, scale: 1, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.6 + i * stagger));
  });
  if (cardIds.length) tweens.push(t.to(`#${cardIds[cardIds.length - 1]}`, { boxShadow: `0 0 90px ${withAlpha(accent, 0.7)}, 0 24px 60px rgba(0,0,0,0.35)`, duration: 0.8, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 0.6 + items.length * stagger));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
